import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import React from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { RestaurantPeriodStatResponse } from '@/api/lib/subscription';

import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';

type Props = {
  data?: RestaurantPeriodStatResponse[];
  isLoading?: boolean;
};

const RestaurantCharts: React.FC<Props> = ({ data, isLoading }) => {
  const hasData = data && data.length > 0;

  const yValues = hasData ? data.map((item) => item.showRestaurantPage) : [];
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const yAxisMargin = (maxY - minY) * 0.1;
  const yAxisMin = Math.max(0, minY - yAxisMargin);
  const yAxisMax = maxY + yAxisMargin;

  return (
    <div className="w-full h-96">
      {isLoading ? (
        <div className="border rounded-md border-dashed border-gray-300 p-10 h-72 w-[90%] flex flex-col gap-5 items-center mx-auto justify-center">
          <Spinner reverse={true} big />
        </div>
      ) : (
        <div className="w-full h-96">
          {hasData ? (
            <ResponsiveContainer width="100%" height="80%">
              <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#F58F7B" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#EF4423" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(dateStr) => format(new Date(dateStr.split(',')[0]), 'dd MMM yyyy', { locale: fr })} />
                <YAxis domain={[yAxisMin, yAxisMax]} />
                <Tooltip labelFormatter={(label) => format(new Date(label.split(',')[0]), 'dd MMM yyyy', { locale: fr })} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="showRestaurantPage"
                  stroke="#EF4423"
                  fillOpacity={1}
                  fill="url(#colorViews)"
                  name={'Affichage de votre fiche restaurant'}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="border rounded-md border-dashed border-gray-300 p-10 h-72 w-[90%] flex flex-col gap-5 items-center mx-auto justify-center">
              <Typography variant="h2">Aucune stats d'affichage de votre fiche restaurant pour ce mois</Typography>
              <Typography variant="paragraph" color="gray">
                Veuillez choisir un mois précédent ou attendre le mois prochain.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RestaurantCharts;
